import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { renderStreamMessage } from "@hotwired/turbo"

// Docs::SidebarComponent drag-and-drop (MC-943).
//
// Desktop-only reorder + relocate of docs directly in the tree, driving the
// single `Docs::Move` service (one POST per drop — never an id sequence).
// Modelled on `boards/column_component/sortable_stage_controller.js`: a
// `matchMedia` gate binds Sortable only at `md+` (touch drags fight the
// tree's vertical scroll — MC-136 / MC-175), and a rejected move rolls the
// DOM back to exactly where it started so the operator never drifts from the
// server. The keyboard/touch path is the "Move to…" dialog on the doc page,
// so this controller carries no accessibility burden of its own.
//
// Each `<ul>` in the tree (the root list and every parent's child list) is a
// Sortable in a shared `group`, so a node drags freely between lists. On drop
// we read the target parent from the destination list's `<details>` and the
// index from Sortable, POST them, and — on success — swap the whole sidebar
// for a fresh server render (a frame-scoped Turbo Stream), so indentation,
// folder/leaf icons and sibling positions all match what `Docs::Move` stored.
//
// Auto-expand (decision 3): hovering a collapsed folder mid-drag opens it
// after a short delay so a drop can land inside in one gesture. Opening a
// `<details>` fires `toggle`, which the co-located `docs--sidebar` controller
// persists to localStorage — so the expand state stays intact across the
// post-move re-render.
//
// Lifecycle (docs/13-ui-conventions.md): connect() is idempotent and binds
// only the viewport listener (Sortables are (de)activated by breakpoint);
// disconnect() tears down every Sortable, the viewport listener and any
// pending expand timer. No DOM reference is held across a navigation.
export default class extends Controller {
  static values = { expandDelay: { type: Number, default: 500 } }

  static DESKTOP_QUERY = "(min-width: 768px)"
  static LIST_SELECTOR = ".docs-sidebar__tree, .docs-sidebar__children"

  connect() {
    this.sortables = []
    this.mql = window.matchMedia(this.constructor.DESKTOP_QUERY)
    this.mqlHandler = this.onViewportChange.bind(this)
    this.mql.addEventListener("change", this.mqlHandler)
    if (this.mql.matches) this.activate()
  }

  disconnect() {
    if (this.mql) this.mql.removeEventListener("change", this.mqlHandler)
    this.deactivate()
  }

  onViewportChange(event) {
    if (event.matches) {
      this.activate()
    } else {
      this.deactivate()
    }
  }

  activate() {
    if (this.sortables.length) return
    this.lists().forEach((list) => {
      this.sortables.push(
        Sortable.create(list, {
          group: "docs-tree",
          animation: 150,
          draggable: ".docs-sidebar__node",
          handle: ".docs-sidebar__drag-handle",
          ghostClass: "docs-sidebar__node--ghost",
          // Nested-sortables settings (SortableJS docs): the tree is lists
          // within list items, so drop onto the body and use a generous swap
          // threshold to land cleanly between and inside nodes.
          fallbackOnBody: true,
          swapThreshold: 0.65,
          onMove: this.onMove.bind(this),
          onEnd: this.onEnd.bind(this)
        })
      )
    })
  }

  deactivate() {
    this.clearExpandTimer()
    this.sortables.forEach((sortable) => sortable.destroy())
    this.sortables = []
  }

  lists() {
    return Array.from(
      this.element.querySelectorAll(this.constructor.LIST_SELECTOR)
    )
  }

  // Fired continuously as the drag hovers. Two jobs: refuse a drop into the
  // dragged node's own subtree (a guaranteed cycle — the server would reject
  // it anyway, but blocking it here spares the round-trip), and arm the
  // auto-expand timer when hovering a collapsed folder.
  onMove(event) {
    if (event.dragged.contains(event.to)) return false

    const details = this.collapsedFolderAt(event.related)
    if (details) {
      this.scheduleExpand(details)
    } else {
      this.clearExpandTimer()
    }
    return true
  }

  async onEnd(event) {
    this.clearExpandTimer()

    // A drop that changed nothing (same list, same slot) needs no server round
    // trip and no re-render.
    if (event.from === event.to && event.oldIndex === event.newIndex) return

    const item = event.item
    const parentId = this.listParentId(event.to)
    const position = event.newDraggableIndex

    let response
    try {
      response = await this.postMove(item.dataset.moveUrl, parentId, position)
    } catch {
      // Network error — nothing changed server-side; roll the node back to the
      // list and slot it came from, following the board's rollback.
      this.revert(item, event.from, event.oldDraggableIndex)
      return
    }

    // Success replaces `#docs-sidebar` with a fresh render; a rejection (cycle,
    // cross-space) replaces `#flash` with the error and returns 422. Render
    // whichever stream came back — so a rejected drag surfaces its message —
    // then, on rejection, roll the DOM back so the view never drifts from the
    // server.
    const body = await response.text()
    if (body.trim()) renderStreamMessage(body)
    if (!response.ok) this.revert(item, event.from, event.oldDraggableIndex)
  }

  async postMove(url, parentId, position) {
    const body = new URLSearchParams()
    // Blank parent_id means "move to root"; the controller reads "" as nil.
    body.set("parent_id", parentId ?? "")
    body.set("position", position)

    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": this.csrfToken()
      },
      body: body.toString()
    })
  }

  // Restore `item` to `list` at `index` — undoes same-list reorders (up and
  // down) and cross-list relocations alike. Detach the node first: a same-list
  // drop leaves it in `list` at its dropped slot, so computing the reference
  // without removing it would count the still-present node and land it one
  // place off on an upward move. With the node detached, `list.children[index]`
  // is the original slot's occupant (an out-of-range/undefined ref appends,
  // correct for a node that was last in its old list).
  revert(item, list, index) {
    item.remove()
    list.insertBefore(item, list.children[index])
  }

  // The new parent's id, or null for the root list. A child list sits inside
  // its parent's `<details data-node-id>`.
  listParentId(list) {
    if (list.classList.contains("docs-sidebar__tree")) return null
    const details = list.closest("details.docs-sidebar__details")
    return details ? details.dataset.nodeId : null
  }

  // The collapsed `<details>` for the node the drag is hovering, or null. A
  // folder must be open (its child `<ul>` visible) before a drop can land
  // inside it.
  collapsedFolderAt(related) {
    const node = related?.closest(".docs-sidebar__node")
    const details = node?.querySelector(":scope > details.docs-sidebar__details")
    return details && !details.open ? details : null
  }

  scheduleExpand(details) {
    if (this.pendingExpand === details) return
    this.clearExpandTimer()
    this.pendingExpand = details
    this.expandTimer = window.setTimeout(() => {
      // Setting `open` fires `toggle`; `docs--sidebar` persists it.
      details.open = true
      this.expandTimer = null
      this.pendingExpand = null
    }, this.expandDelayValue)
  }

  clearExpandTimer() {
    if (this.expandTimer) {
      window.clearTimeout(this.expandTimer)
      this.expandTimer = null
    }
    this.pendingExpand = null
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.getAttribute("content") : ""
  }
}
